import React, { useState } from 'react';

import {
  STATUS_META,
  STATUS_PILLS,
} from '../../../../components/procedures/widget/dental/summaryHelpers';

/**
 * TabletChartSummaryOverlay — Variant M's per-tooth summary view.
 *
 * Mounts as an `absolute inset-0` overlay INSIDE the tablet chart pane
 * when the dentist taps the `📋 Summary` button in the chart header.
 * Covers only the chart area — the procedure rail next door stays
 * visible and actionable, matching the desktop mental model.
 *
 * Wraps the existing `FindingsSummary` body so the per-tooth data
 * projection + Problem/Plan/Status rendering stays single-sourced. Adds
 * an overlay header (title + count + ← Back to chart) and a top filter
 * pill row (All + the shared STATUS_PILLS set: Open · Planned ·
 * In progress · Done) that flows into FindingsSummary via the new
 * optional `controlledStatusFilter` / `onStatusFilterChange` props.
 *
 * Props
 *   findingsSummary  — the rendered `<FindingsSummary />` element. The
 *                      host passes a render-prop variant so we can
 *                      inject the controlled filter without
 *                      re-implementing the data wiring up here.
 *   teethWithFindingsCount — integer, shown in the overlay header.
 *   counts           — { all, open, in_progress, done } from the same
 *                      projection FindingsSummary uses. Drives the pill
 *                      labels.
 *   onBack           — () => void, closes the overlay.
 *   isDark           — boolean.
 */
export default function TabletChartSummaryOverlay({
  renderFindings,
  teethWithFindingsCount = 0,
  // Status counts from the shared `buildSummary` selector. No default —
  // a missing prop must read as 0, never masquerade as real all-zeros
  // data (the bug that hid the desktop-vs-mobile drift). Always passed
  // by the host (ToothChartContainer).
  counts,
  onBack,
  isDark = false,
  // G — optional controlled filter so the host (ToothChartContainer) can
  // share one statusFilter between this overlay AND the chart. Falls back to
  // internal state when not provided.
  statusFilter: controlledStatusFilter,
  onStatusFilterChange,
}) {
  const [internalStatusFilter, setInternalStatusFilter] = useState('all');
  const isControlled = typeof controlledStatusFilter === 'string'
    && typeof onStatusFilterChange === 'function';
  const statusFilter = isControlled ? controlledStatusFilter : internalStatusFilter;
  const setStatusFilter = isControlled ? onStatusFilterChange : setInternalStatusFilter;

  // Buckets come from the shared STATUS_PILLS list (+ a leading `all`
  // control) so this overlay always exposes the identical filters as the
  // desktop StatusBanner — including the Planned chip. Labels and dot
  // colours are read from STATUS_META, so nothing can drift.
  const pills = [
    { id: 'all', label: 'All', dot: null },
    ...STATUS_PILLS.map((id) => ({
      id,
      label: STATUS_META[id].label,
      dot: STATUS_META[id].dotClass,
    })),
  ];

  return (
    <div
      className={[
        'absolute inset-0 z-10 flex flex-col',
        'bg-white dark:bg-slate-900',
      ].join(' ')}
      role="region"
      aria-label="Per-tooth summary"
    >
      {/* Header strip — back button + count */}
      <div
        className={[
          'shrink-0 px-3 py-2 flex items-center justify-between gap-2',
          'border-b border-teal-200 dark:border-teal-800',
          'bg-teal-50 dark:bg-teal-900/30',
        ].join(' ')}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-teal-600 text-white text-xs font-bold"
          >
            📋
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-none text-slate-900 dark:text-slate-100">
              Per-tooth summary
            </div>
            <div className="text-[11px] text-slate-600 dark:text-slate-300 mt-0.5">
              {teethWithFindingsCount} {teethWithFindingsCount === 1 ? 'tooth' : 'teeth'} with findings
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onBack}
          className={[
            'rounded-md px-2.5 py-1 text-[11px] font-medium',
            'border border-slate-300 dark:border-slate-600',
            'bg-white dark:bg-slate-800',
            'text-slate-700 dark:text-slate-200',
            'hover:bg-slate-50 dark:hover:bg-slate-700',
          ].join(' ')}
          aria-label="Back to chart"
        >
          ← Back to chart
        </button>
      </div>

      {/* Filter pills — controlled. Drive the inner FindingsSummary's
          status filter via the render-prop receiving { statusFilter }. */}
      <div
        className={[
          'shrink-0 px-3 py-2 flex items-center gap-1.5 flex-wrap',
          'border-b border-slate-200 dark:border-slate-700',
          'bg-white dark:bg-slate-900',
        ].join(' ')}
        role="group"
        aria-label="Filter by status"
      >
        {pills.map((p) => {
          const active = statusFilter === p.id;
          const count = counts?.[p.id] ?? 0;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setStatusFilter(p.id)}
              className={[
                'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold border',
                active
                  ? 'bg-teal-600 border-teal-700 text-white'
                  : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700',
              ].join(' ')}
              aria-pressed={active}
            >
              {p.dot && (
                <span
                  aria-hidden="true"
                  className={['inline-block w-1.5 h-1.5 rounded-full', p.dot].join(' ')}
                />
              )}
              <span>{p.label}</span>
              <span className={active ? 'opacity-90' : 'opacity-60'}>· {count}</span>
            </button>
          );
        })}
      </div>

      {/* Body — host injects FindingsSummary with the controlled filter. */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-white dark:bg-slate-900">
        {typeof renderFindings === 'function'
          ? renderFindings({ statusFilter, setStatusFilter, isDark })
          : renderFindings}
      </div>
    </div>
  );
}
